import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/Button';
import type { PositionStatus } from '@/hooks/useUserPosition';
import { canOpenAppSettings, openAppSettings } from '@/lib/location';

type Platform = 'ios' | 'android' | 'web';

export interface NudgeCopy {
  title: string;
  body: string;
  /** 'request' = pedido do sistema / diálogo do Google; 'settings' = Ajustes. */
  action: { label: string; kind: 'request' | 'settings' } | null;
  hint: string | null;
}

const WHY = 'Assim mostramos o eletroposto onde você está e evitamos iniciar a recarga no lugar errado.';

/**
 * Texto do aviso por estado × plataforma (puro — testado em
 * location-nudge.smoke.test.tsx). `null` = estado que não pede aviso.
 * `tried` = o cliente já tocou no botão uma vez e o estado não mudou.
 */
export function nudgeCopy(
  status: PositionStatus,
  platform: Platform,
  opts: { canOpenSettings: boolean; tried: boolean; locating?: boolean }
): NudgeCopy | null {
  const settings = opts.canOpenSettings
    ? ({ label: 'Abrir Ajustes', kind: 'settings' } as const)
    : null;
  switch (status) {
    case 'prompt':
      return {
        title: 'Ative sua localização',
        body: WHY,
        action: { label: 'Ativar localização', kind: 'request' },
        hint: null
      };
    case 'off':
      if (platform === 'ios') {
        return {
          title: 'Ligue a localização do iPhone',
          body: WHY,
          action: settings,
          hint: 'Em Ajustes › Privacidade e Segurança › Serviços de Localização.'
        };
      }
      return {
        title: 'Ligue a localização do celular',
        body: WHY,
        action: { label: 'Ligar localização', kind: 'request' },
        hint: opts.tried
          ? 'Se não abrir, puxe a barra do topo da tela e toque em “Localização”.'
          : null
      };
    case 'denied':
      if (platform === 'web') {
        return {
          title: 'Libere a localização',
          body: WHY,
          action: null,
          hint: 'Permita a localização deste site nas configurações do navegador.'
        };
      }
      return {
        title: 'Libere a localização pro app Bow',
        body: WHY,
        action: settings,
        hint:
          platform === 'ios'
            ? 'Em Ajustes › Bow, toque em Localização e escolha “Durante o Uso do App”.'
            : 'Em Ajustes › Permissões › Localização, escolha “Permitir durante o uso do app”.'
      };
    case 'granted':
      if (opts.locating) {
        return {
          title: 'Procurando sua localização…',
          body: 'Só um instante.',
          action: null,
          hint: null
        };
      }
      // Permissão dada, mas a leitura falhou (ambiente fechado, GPS sem sinal).
      return {
        title: 'Não achamos sua localização agora',
        body: 'Confira se a localização do celular está ligada e tente de novo.',
        action: { label: 'Tentar de novo', kind: 'request' },
        hint: null
      };
    default:
      return null;
  }
}

interface Props {
  status: PositionStatus;
  locating: boolean;
  onRequest: () => Promise<PositionStatus>;
  className?: string;
}

/**
 * Aviso de localização da Home/ConfigureCharge — aparece TODA vez que o app
 * abre sem localização e não tem botão de fechar: é a insistência pedida em
 * 2026-10-08 (clientes iniciando recarga no eletroposto errado). Some sozinho
 * quando a localização chega (o hook reconsulta ao voltar dos Ajustes).
 */
export function LocationNudge({ status, locating, onRequest, className = '' }: Props) {
  const [tried, setTried] = useState(false);
  const platform = Capacitor.getPlatform() as Platform;
  const copy = nudgeCopy(status, platform, {
    canOpenSettings: canOpenAppSettings(),
    tried,
    locating
  });
  if (!copy) return null;

  async function onAction() {
    if (!copy?.action) return;
    if (copy.action.kind === 'settings') {
      await openAppSettings();
    } else {
      await onRequest();
    }
    setTried(true);
  }

  return (
    <div
      className={`flex flex-col gap-3 rounded-[20px] bg-white p-4 ${className}`}
      style={{ border: '1.5px solid rgba(0,192,114,0.35)', boxShadow: '0 6px 18px rgba(0,27,116,0.08)' }}
      role="status"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[13px]"
          style={{ background: 'rgba(0,192,114,0.12)' }}
        >
          <PinIcon />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="leading-tight text-bow-royal"
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 600,
              fontSize: 15.5,
              letterSpacing: '-0.02em'
            }}
          >
            {copy.title}
          </div>
          <p className="mt-1 text-[12.5px] leading-snug text-bow-royal/65">{copy.body}</p>
          {copy.hint && (
            <p className="mt-1.5 text-[12px] font-medium leading-snug text-bow-royal/80">
              {copy.hint}
            </p>
          )}
        </div>
      </div>
      {copy.action && (
        <Button size="md" onClick={() => void onAction()} disabled={locating}>
          {locating ? 'Localizando…' : copy.action.label}
        </Button>
      )}
    </div>
  );
}

function PinIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={21}
      height={21}
      fill="none"
      stroke="#008A4F"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </svg>
  );
}
